import React from 'react';
import { Tv, MapPin, Rocket, ArrowRight, Play, ChevronDown, Clock, Flame } from 'lucide-react';
import type { PublisherConfig } from '../../types/selfService';
import { displayedName } from '../../data/publisherConfigs';
import { accentVars, tintFromAccent } from './steps/_shared';

interface SelfServiceLandingProps {
  config: PublisherConfig;
  onGetStarted: () => void;
}

const STEPS = [
  { icon: Tv, title: 'Add business' },
  { icon: MapPin, title: 'Set targeting' },
  { icon: Play, title: 'Upload ad' },
  { icon: Rocket, title: 'Launch' },
];

// CTV-567 dynamic page sizing. The TV hero is the tallest thing on the landing;
// left at its natural width it pushes the "How it works" tracker below the fold
// on short laptops. We cap the hero's WIDTH by the available viewport height so
// it shrinks on short screens instead — its height follows via the natural
// aspect ratio, which keeps the percentage-positioned screen overlay aligned.
// HERO_RESERVED_PX is the vertical budget (header + CTA + "How it works" + gaps)
// that must share the viewport with the TV; tune it if those sections change.
const TV_ASPECT = 1932 / 1150; // natural pixel size of the TV mockup image
// Because the hero's aspect ratio is fixed, its rendered height === (100dvh -
// HERO_RESERVED_PX). Reserve just enough for the header + CTA + "How it works"
// tracker (+ a little slack) so the TV stays as large as possible while keeping
// the tracker above the fold. At 720px tall this leaves ~50px below the tracker.
const HERO_RESERVED_PX = 360;
const HERO_MAX_WIDTH = `min(100%, calc((100dvh - ${HERO_RESERVED_PX}px) * ${TV_ASPECT}))`;

const SelfServiceLanding: React.FC<SelfServiceLandingProps> = ({ config, onGetStarted }) => {
  const accent = config.accentColor || config.primaryColor;
  const primary = config.primaryColor || accent;
  const label = displayedName(config);

  return (
    <div className="relative" style={accentVars(accent, config.primaryColor)}>
      <FaceliftKeyframes />

      {/* Aurora backdrop — bleeds edge-to-edge behind hero */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[850px] overflow-hidden"
        style={{ left: 'calc(-50vw + 50%)', right: 'calc(-50vw + 50%)' }}
      >
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl opacity-70"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.22)}, transparent 70%)`,
          }}
        />
        <div
          className="absolute top-24 left-[8%] w-[420px] h-[420px] rounded-full blur-3xl opacity-60"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(primary, 0.18)}, transparent 70%)`,
          }}
        />
        <div
          className="absolute top-40 right-[6%] w-[360px] h-[360px] rounded-full blur-3xl opacity-70"
          style={{
            background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.16)}, transparent 70%)`,
          }}
        />
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* Smart TV mockup — centered */}
        <div className="relative w-full pt-2 pb-2 px-1">
          <TvMockup
            accent={accent}
            primary={primary}
            logoUrl={config.logoUrl}
            logoAlt={`${label} logo`}
            headlineLines={['Your business', `on ${label}`]}
            subtitleEyebrow="Smart TV · Roku · Fire TV · Apple TV · Google TV"
            subtitle="Connect with your customers on streaming TV"
          />
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center justify-center gap-2.5 pb-5 px-1">
          <div className="relative">
            {/* Pulsing aura behind the button */}
            <span
              aria-hidden
              className="absolute inset-0 -m-4 rounded-full pointer-events-none"
              style={{
                background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.45)}, transparent 70%)`,
                animation: 'ssl-cta-pulse 2.8s ease-in-out infinite',
              }}
            />
            <button
              type="button"
              onClick={onGetStarted}
              className="group relative inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-full text-[16px] font-semibold tracking-tight text-white transition-all hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] overflow-hidden"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${primary})`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.3) inset, 0 0 0 1px rgba(255,255,255,0.12) inset, 0 20px 44px -12px ${primary}, 0 8px 22px -8px ${tintFromAccent(accent, 0.6)}`,
              }}
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full"
                style={{
                  background:
                    'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)',
                  animation: 'ssl-shimmer 5.5s ease-in-out infinite',
                }}
              />
              <span className="relative inline-flex items-center gap-2.5">
                Get started
                <ArrowRight className="w-[18px] h-[18px] transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-[0.1em]">
            <Clock className="w-3 h-3" />
            Takes about 5 minutes
          </span>
        </div>

        {/* How it works — horizontal step tracker */}
        <div className="mb-10 max-w-3xl mx-auto px-1">
          <div
            className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] mb-4"
            style={{ color: accent }}
          >
            How it works
          </div>
          <div className="relative grid grid-cols-4 gap-2">
            {/* Connector line behind the dots */}
            <div
              aria-hidden
              className="absolute top-7 h-px"
              style={{
                left: '12.5%',
                right: '12.5%',
                backgroundImage: `linear-gradient(90deg, ${tintFromAccent(accent, 0.4)} 50%, transparent 50%)`,
                backgroundSize: '8px 1px',
                animation: 'ssl-march 1.6s linear infinite',
              }}
            />
            {STEPS.map((step, i) => (
              <div key={step.title} className="relative flex flex-col items-center text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center bg-white shrink-0 z-10 relative"
                  style={{
                    border: `1px solid ${tintFromAccent(accent, 0.35)}`,
                    color: accent,
                    boxShadow: `0 1px 0 rgba(255,255,255,0.9) inset, 0 8px 24px -10px ${tintFromAccent(primary, 0.55)}`,
                  }}
                >
                  <step.icon className="w-5 h-5" />
                  <div
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{
                      background: `linear-gradient(135deg, ${accent}, ${primary})`,
                      boxShadow: `0 2px 6px -2px ${tintFromAccent(primary, 0.6)}`,
                    }}
                  >
                    {i + 1}
                  </div>
                </div>
                <div className="text-[12px] font-semibold tracking-tight text-slate-900 mt-3 leading-snug">
                  {step.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-14 max-w-3xl mx-auto px-1">
          <h2
            className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] mb-5"
            style={{ color: accent }}
          >
            FAQ
          </h2>
          <FaqList accentColor={accent} publisherName={label} />
        </div>
      </div>
    </div>
  );
};

const FaceliftKeyframes: React.FC = () => (
  <style>{`
    @keyframes ssl-shimmer {
      0% { transform: translateX(-100%); }
      60% { transform: translateX(100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes ssl-cta-pulse {
      0%, 100% { transform: scale(1); opacity: 0.55; }
      50%      { transform: scale(1.18); opacity: 0.85; }
    }
    @keyframes ssl-march {
      from { background-position: 0 0; }
      to { background-position: 16px 0; }
    }
    @keyframes ssl-march-v {
      from { background-position: 0 0; }
      to { background-position: 0 16px; }
    }
    @keyframes ssl-pulse-dot {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }
    @keyframes ssl-underline-punch {
      0%, 9%   { opacity: 1; transform: scaleX(0); }
      11%      { opacity: 1; transform: scaleX(1); }
      28%      { opacity: 1; transform: scaleX(1); }
      33%      { opacity: 0; transform: scaleX(1); }
      100%     { opacity: 0; transform: scaleX(0); }
    }
    @keyframes ssl-tv-slide-2 {
      0%, 35% { opacity: 0; transform: translateY(6px) scale(0.98); }
      38%, 58% { opacity: 1; transform: translateY(0) scale(1); }
      61%, 100% { opacity: 0; transform: translateY(6px) scale(0.98); }
    }
    @keyframes ssl-chip-pop {
      0%, 36%   { opacity: 0; transform: scale(0.55) translateY(10px); }
      39%       { opacity: 1; transform: scale(1.12) translateY(-2px); }
      42%       { opacity: 1; transform: scale(1) translateY(0); }
      56%       { opacity: 1; transform: scale(1) translateY(0); }
      60%       { opacity: 0; transform: scale(0.95) translateY(-4px); }
      100%      { opacity: 0; transform: scale(0.95) translateY(-4px); }
    }
    @keyframes ssl-tv-slide-3 {
      0%, 63% { opacity: 0; transform: translateY(6px) scale(0.98); }
      66%, 89% { opacity: 1; transform: translateY(0) scale(1); }
      92%, 100% { opacity: 0; transform: translateY(6px) scale(0.98); }
    }
    @keyframes ssl-slide3-word {
      0%, 1% { opacity: 0; transform: translateY(12px) scale(0.94); }
      6%     { opacity: 1; transform: translateY(-2px) scale(1.02); }
      10%    { opacity: 1; transform: translateY(0) scale(1); }
      82%    { opacity: 1; transform: translateY(0) scale(1); }
      88%, 100% { opacity: 0; transform: translateY(-4px) scale(0.96); }
    }
    /* Channel bug grows out of nothing in the bottom-right corner once the
       channel-name line has landed (~11%): it scales up from 0, overshoots
       slightly past its final size, then eases back down to rest in a single
       settle — one overshoot, no oscillation/jiggle. Holds through the
       pitch slides, then fades off before the loop restarts so it stamps in
       fresh each cycle. Origin is bottom-right (set on the element) so the
       scale-from-0 expands out of the corner and never overflows the
       right/bottom edges; the motion is logo-size-agnostic. The whole grow +
       overshoot + settle is ONE continuous segment (11%→19%) driven by an
       easeOutBack curve: it springs up past final size mid-flight then rolls
       smoothly back to rest. The peak is just a turnaround point — never a
       keyframed stop — so it never sits at the overshoot size. */
    @keyframes ssl-bug-stamp {
      0%, 10%   { opacity: 0; transform: scale(0); }
      11%       { opacity: 1; transform: scale(0); animation-timing-function: cubic-bezier(0.34, 1.45, 0.64, 1); }
      19%       { opacity: 0.95; transform: scale(1); }
      90%       { opacity: 0.95; transform: scale(1); }
      96%, 100% { opacity: 0; transform: scale(1); }
    }
  `}</style>
);

// Brand glyphs for the streaming-platform pills. Paths are inlined so the page
// has no external icon-CDN dependency.
const PlatformIcon: React.FC<{ name: string; className?: string }> = ({ name, className }) => {
  const cls = className ?? 'w-[1em] h-[1em] shrink-0';
  switch (name) {
    case 'Roku':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={cls}>
          <path d="M16.34 9.853l-2.254 2.254v-2.26H12.13v5.744h1.957v-2.33l2.353 2.33h2.46l-2.988-2.99 2.477-2.476v3.411c0 1.133.679 2.177 2.393 2.177.815 0 1.56-.462 1.922-.88l.88.759H24v-5.74h-1.951v3.718c-.22.384-.528.627-1.002.627-.482 0-.703-.286-.703-1.198V9.853zm-4.591 2.869A3.004 3.004 0 1 1 8.738 9.73a2.997 2.997 0 0 1 3.011 2.99m-3.011-1.57c-.518 0-.956.704-.956 1.572 0 .867.438 1.57.956 1.57.528 0 .968-.702.968-1.57 0-.869-.438-1.572-.968-1.572zm-2.206 4.447H4.313L2.55 13.153h-.594v2.44H0V8.26h2.8c1.616 0 2.935 1.1 2.935 2.45 0 .826-.505 1.562-1.273 2.013l2.07 2.875m-2.75-4.888A1.226 1.226 0 0 0 2.56 9.478h-.604v2.453h.605a1.225 1.225 0 0 0 1.22-1.221Z" />
        </svg>
      );
    case 'Apple TV':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={cls}>
          <path d="M20.57 17.735h-1.815l-3.34-9.203h1.633l2.02 5.987c.075.231.273.9.586 2.012l.297-.997.33-1.006 2.094-6.004H24zm-5.344-.066a5.76 5.76 0 0 1-1.55.207c-1.23 0-1.84-.693-1.84-2.087V9.646h-1.063V8.532h1.121V7.081l1.476-.602v2.062h1.707v1.113H13.38v5.805c0 .446.074.75.214.932.14.182.396.264.75.264.207 0 .495-.041.883-.115zm-7.29-5.343c.017 1.764 1.55 2.358 1.567 2.366-.017.042-.248.842-.808 1.658-.487.71-.99 1.418-1.79 1.435-.783.016-1.03-.462-1.93-.462-.89 0-1.17.445-1.913.478-.758.025-1.344-.775-1.838-1.484-.998-1.451-1.765-4.098-.734-5.88.51-.89 1.426-1.451 2.416-1.46.75-.016 1.468.512 1.93.512.461 0 1.327-.627 2.234-.536.38.016 1.452.157 2.136 1.154-.058.033-1.278.743-1.27 2.219M6.468 7.988c.404-.495.685-1.18.61-1.864-.585.025-1.294.388-1.723.883-.38.437-.71 1.138-.619 1.806.652.05 1.328-.338 1.732-.825Z" />
        </svg>
      );
    case 'Google TV':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={cls}>
          <path d="M3.11 3.245A3.117 3.117 0 0 0 0 6.355V13.7a1.87 1.87 0 0 0 1.878 1.878h2.588V5.124c0-.73.313-1.399.814-1.879zm3.944 0a1.87 1.87 0 0 0-1.879 1.879V7.71h16.947v.021c.73 0 1.398.313 1.878.814v-2.19a3.117 3.117 0 0 0-3.11-3.11zm12.48 5.176v10.455c0 .73-.313 1.399-.814 1.879h2.17a3.117 3.117 0 0 0 3.11-3.11V10.3a1.87 1.87 0 0 0-1.878-1.878zM0 15.475v2.17a3.117 3.117 0 0 0 3.11 3.11h13.836a1.87 1.87 0 0 0 1.878-1.879V16.29H1.878c-.73 0-1.398-.314-1.878-.814" />
        </svg>
      );
    case 'Fire TV':
      return <Flame className={cls} />;
    case 'Smart TV':
    default:
      return <Tv className={cls} />;
  }
};

interface TvMockupProps {
  accent: string;
  primary: string;
  headlineLines: string[];
  subtitle: string;
  subtitleEyebrow?: string;
  logoUrl?: string;
  logoAlt?: string;
}

const TvMockup: React.FC<TvMockupProps> = ({ accent, primary, headlineLines, subtitle, subtitleEyebrow, logoUrl, logoAlt }) => {
  const CYCLE_SEC = 18;
  const INTRA_STAGGER_SEC = 0.18;
  const LINE_PAUSE_SEC = 1.0;

  // A single all-caps word on its own line (e.g. "LIVE") is treated as the
  // dramatic punch — it gets the scale-snap entrance and the underline.
  const lineMeta = headlineLines.map((line) => {
    const words = line.split(' ');
    const isImpactLine =
      words.length === 1 && words[0].length > 1 && words[0] === words[0].toUpperCase();
    return { words, isImpactLine };
  });

  // Pre-compute per-word delay percentages so all animations share a single
  // synchronized 12s cycle. Stagger is baked into the keyframes, not into
  // animation-delay — otherwise each word loops on its own clock and they
  // drift apart, leaving the subtitle still on screen when the headline
  // restarts.
  const wordMeta: { popStartPct: number; isImpact: boolean }[] = [];
  {
    let gi = 0;
    lineMeta.forEach((lm, lineIdx) => {
      lm.words.forEach(() => {
        const delaySec = gi * INTRA_STAGGER_SEC + lineIdx * LINE_PAUSE_SEC;
        wordMeta.push({
          popStartPct: (delaySec / CYCLE_SEC) * 100,
          isImpact: lm.isImpactLine,
        });
        gi++;
      });
    });
  }

  const HOLD_END_PCT = 28;
  const FADE_END_PCT = 33;
  const wordKeyframesCss = wordMeta.map((m, i) => {
    const s = m.popStartPct;
    if (m.isImpact) {
      return `@keyframes ssl-w-${i} {
        0%, ${s.toFixed(2)}% { opacity: 0; transform: scale(1.55) translateY(20px); }
        ${(s + 3).toFixed(2)}% { opacity: 1; transform: scale(0.84) translateY(0); }
        ${(s + 6).toFixed(2)}% { opacity: 1; transform: scale(1.04) translateY(0); }
        ${(s + 10).toFixed(2)}% { opacity: 1; transform: scale(1) translateY(0); }
        ${HOLD_END_PCT}% { opacity: 1; transform: scale(1) translateY(0); }
        ${FADE_END_PCT}% { opacity: 0; transform: scale(1) translateY(-6px); }
        100% { opacity: 0; transform: scale(1) translateY(-6px); }
      }`;
    }
    return `@keyframes ssl-w-${i} {
      0%, ${s.toFixed(2)}% { opacity: 0; transform: translateY(14px); }
      ${(s + 4).toFixed(2)}% { opacity: 1; transform: translateY(-2px); }
      ${(s + 8).toFixed(2)}% { opacity: 1; transform: translateY(0); }
      ${HOLD_END_PCT}% { opacity: 1; transform: translateY(0); }
      ${FADE_END_PCT}% { opacity: 0; transform: translateY(-6px); }
      100% { opacity: 0; transform: translateY(-6px); }
    }`;
  }).join('\n');

  let wordIndex = 0;
  return (
    <div
      className="relative mx-auto"
      style={{ transform: 'translateX(4.5%)', width: HERO_MAX_WIDTH }}
    >
      <style>{wordKeyframesCss}</style>
      {/* Soft glow behind the TV */}
      <div
        aria-hidden
        className="absolute inset-0 blur-3xl opacity-50 -z-10"
        style={{
          background: `radial-gradient(closest-side, ${tintFromAccent(accent, 0.35)}, transparent 70%)`,
        }}
      />
      {/* TV chrome — external image (includes stand). The screen area is
          overlaid via the absolutely positioned wrapper below. */}
      <img
        src="https://ambioedu.com/wp-content/webp-express/webp-images/themes/ambio/assets/images/home-slider-tv1.png.webp"
        alt=""
        aria-hidden
        className="block w-full h-auto relative"
      />
      {/* Screen overlay — positioned over the TV image's screen rectangle.
          Tweak these percentages if the image's screen bounds shift. */}
      <div
        className="absolute overflow-hidden"
        style={{
          // CTV-567: insets are PURE percentages so the screen rectangle scales
          // with the TV. The previous `calc(% - Npx)` nudges were only correct at
          // full size — the fixed px became a larger fraction as the TV shrank,
          // drifting the gradient off the bezel (overhang right, gap at bottom).
          // Each px was folded into its % equivalent at the calibrated full size
          // (888×529): -2px→0.38%h, -1px→0.11%w, -8px→0.90%w, +1px→0.19%h.
          top: '0.62%',
          left: '1.89%',
          right: '10.1%',
          bottom: '16.19%',
          background: `linear-gradient(135deg, ${tintFromAccent(primary, 0.95)}, ${tintFromAccent(accent, 0.85)})`,
          // make the screen a query container so everything on it sizes in cqw
          // (% of the screen's own width) and scales with the TV, not the browser
          // viewport. 100cqw === this screen's width.
          containerType: 'inline-size',
        }}
      >
        {/* Scanline / bloom overlay */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.25), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(0,0,0,0.15), transparent 60%)',
          }}
        />
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Cycling slides on screen */}
          <div className="absolute inset-0 flex items-center justify-center px-[4.1cqw] text-center pointer-events-none">
            <h2
              className="absolute font-semibold tracking-tight text-white leading-[1.6]"
              style={{
                fontSize: 'clamp(20px, 8.3cqw, 64px)',
                textShadow: '0 2px 16px rgba(0,0,0,0.3)',
              }}
            >
              {headlineLines.map((line, lineIdx) => (
                <div key={lineIdx}>
                  {line.split(' ').map((word, wIdx, lineArr) => {
                    const globalI = wordIndex++;
                    const isImpactWord = wordMeta[globalI].isImpact;
                    const shouldUnderline = isImpactWord;
                    return (
                      <React.Fragment key={wIdx}>
                        <span
                          className="relative inline-block"
                          style={{
                            animation: `ssl-w-${globalI} ${CYCLE_SEC}s ease-out infinite`,
                            ...(isImpactWord && {
                              color: '#ef4444',
                              fontStyle: 'italic',
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              textShadow:
                                '0 0 28px rgba(239, 68, 68, 0.65), 0 0 8px rgba(239, 68, 68, 0.4), 0 2px 16px rgba(0,0,0,0.3)',
                            }),
                          }}
                        >
                          {word}
                          {shouldUnderline && (
                            <svg
                              aria-hidden
                              className="absolute"
                              style={{
                                left: 0,
                                right: 0,
                                bottom: '0.15em',
                                width: '100%',
                                height: '0.26em',
                                overflow: 'visible',
                                opacity: 1,
                                transform: 'scaleX(0)',
                                transformOrigin: 'left',
                                animation: `ssl-underline-punch ${CYCLE_SEC}s ease-out infinite`,
                              }}
                              viewBox="0 0 100 10"
                              preserveAspectRatio="none"
                            >
                                <path
                                  d="M 2 6 Q 50 3 98 6"
                                  stroke="currentColor"
                                  strokeWidth="2.8"
                                  strokeLinecap="round"
                                  fill="none"
                                  vectorEffect="non-scaling-stroke"
                                />
                              </svg>
                            )}
                          </span>
                          {wIdx < lineArr.length - 1 ? ' ' : ''}
                        </React.Fragment>
                      );
                  })}
                </div>
              ))}
            </h2>
            <div
              className="absolute max-w-[92%] text-center text-white"
              style={{
                animation: `ssl-tv-slide-2 ${CYCLE_SEC}s ease-in-out infinite`,
              }}
            >
              <div
                className="font-bold leading-[1.15]"
                style={{
                  fontSize: 'clamp(14px, 5.7cqw, 44px)',
                  textShadow: '0 2px 18px rgba(0,0,0,0.4)',
                }}
              >
                {subtitle}
              </div>
              {subtitleEyebrow && (
                <div className="flex flex-wrap items-start justify-center gap-[1.5cqw] mt-[2.6cqw]">
                  {subtitleEyebrow.split(/\s*·\s*/).map((platform, i) => (
                    <span
                      key={platform}
                      className="inline-flex flex-col items-center justify-center gap-[0.8cqw] w-[12.4cqw] px-[1cqw] py-[1.3cqw] rounded-2xl text-white"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.24)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        animation: `ssl-chip-pop ${CYCLE_SEC}s ease-out infinite`,
                        animationDelay: `${i * 0.14}s`,
                      }}
                    >
                      <PlatformIcon name={platform} className="w-[4.1cqw] h-[4.1cqw] shrink-0" />
                      <span
                        className="font-semibold leading-none"
                        style={{ fontSize: 'clamp(8px, 1.7cqw, 13px)' }}
                      >
                        {platform}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Slide 3 — "Your ad here" placeholder */}
            <div
              className="absolute flex flex-col items-center"
              style={{
                animation: `ssl-tv-slide-3 ${CYCLE_SEC}s ease-in-out infinite`,
              }}
            >
              <div
                className="relative flex flex-col items-center gap-[1cqw] px-[5.2cqw] py-[3.1cqw] rounded-2xl"
                style={{
                  border: '2px dashed rgba(255,255,255,0.55)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                }}
              >
                <div
                  className="font-bold uppercase text-white"
                  style={{
                    fontSize: 'clamp(16px, 6.5cqw, 50px)',
                    letterSpacing: '0.14em',
                    textShadow: '0 2px 18px rgba(0,0,0,0.4)',
                  }}
                >
                  Your Ad Here
                </div>
                <div
                  className="font-medium text-white/85"
                  style={{
                    fontSize: 'clamp(11px, 2.6cqw, 20px)',
                    letterSpacing: '0.03em',
                  }}
                >
                  {[
                    { word: 'Built', delay: 12.0 },
                    { word: 'for', delay: 12.15 },
                    { word: 'you.', delay: 12.3 },
                    { word: 'In', delay: 12.6 },
                    { word: 'minutes.', delay: 12.75 },
                  ].map((w, i, arr) => (
                    <React.Fragment key={i}>
                      <span
                        className="inline-block"
                        style={{
                          opacity: 0,
                          animation: `ssl-slide3-word ${CYCLE_SEC}s ease-out infinite`,
                          animationDelay: `${w.delay}s`,
                        }}
                      >
                        {w.word}
                      </span>
                      {i < arr.length - 1 ? ' ' : ''}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Channel bug — bottom right watermark */}
          {logoUrl && (
            <img
              src={logoUrl}
              alt={logoAlt}
              className="absolute bottom-[1.8cqw] right-[1.8cqw] h-[7.2cqw] w-auto object-contain opacity-95 drop-shadow-md pointer-events-none"
              style={{ animation: `ssl-bug-stamp ${CYCLE_SEC}s ease-out infinite`, transformOrigin: 'bottom right' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface FaqItem {
  q: string;
  a: React.ReactNode;
}

const buildFaq = (publisherName: string): FaqItem[] => [
  {
    q: 'What is connected TV (CTV)?',
    a: (
      <>
        Connected TV is video advertising delivered through streaming apps on smart TVs, Roku, Fire
        TV, Apple TV, and similar devices. Your commercial plays just like a traditional TV ad,
        but only to viewers in {publisherName}'s local market.
      </>
    ),
  },
  {
    q: 'How long until my campaign goes live?',
    a: (
      <>
        Most campaigns are reviewed and approved within 24–48 hours. You pick a start date that's at
        least 3 days out so {publisherName} has time to review your commercial.
      </>
    ),
  },
  {
    q: "What if I don't have a commercial?",
    a: (
      <>
        No problem. {publisherName} offers a quick, budget-friendly production option that turns
        your story into a polished CTV-ready commercial in minutes — not weeks.
      </>
    ),
  },
  {
    q: 'What format does my video need to be in?',
    a: (
      <>
        MP4 at 1920×1080 (1080p) minimum, 15 or 30 seconds long, with audio. Up to 500 MB.
      </>
    ),
  },
  {
    q: 'Can I cancel a campaign once it starts?',
    a: (
      <>
        Live campaigns run through their scheduled end date. If you need to make a change, contact{' '}
        {publisherName} directly — their team can help.
      </>
    ),
  },
];

const FaqList: React.FC<{ accentColor: string; publisherName: string }> = ({ accentColor, publisherName }) => {
  const items = React.useMemo(() => buildFaq(publisherName), [publisherName]);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  return (
    <div
      className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden divide-y divide-slate-200/70"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={item.q} className="relative">
            <span
              aria-hidden
              className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r transition-opacity"
              style={{
                backgroundColor: accentColor,
                opacity: isOpen ? 1 : 0,
              }}
            />
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50/60"
              aria-expanded={isOpen}
            >
              <span
                className="flex-1 text-[14px] font-semibold tracking-tight text-slate-900"
              >
                {item.q}
              </span>
              <ChevronDown
                className="w-4 h-4 transition-transform duration-200 shrink-0"
                style={{
                  color: accentColor,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </button>
            <div
              className="overflow-hidden transition-all duration-300 ease-out"
              style={{
                maxHeight: isOpen ? '400px' : '0px',
                opacity: isOpen ? 1 : 0,
              }}
            >
              <div className="px-5 pb-4 -mt-1 text-[13px] text-slate-600 leading-relaxed">
                {item.a}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SelfServiceLanding;
